export function originalXeroEvidence(documents: Evidence[], checksum: string): Omit<Evidence, "title"> {
  const matches = documents.filter((d) => d.checksum === checksum);
  if (matches.length !== 1)
    throw Error("Retain one accessible scanned version of each original source and bank witness.");
  const { title: _, ...evidence } = matches[0]!;
  return evidence;
}

export type Invoice = {
  sourceId: string;
  kind: string;
  status: string;
  reference: string;
  contact: string;
  date: string;
  total: string;
  paid: string;
  due: string;
};
export type CreditApplication = {
  sourceId: string;
  creditSourceId: string;
  debitSourceId: string;
  amount: string;
  effectiveDate: string;
};
export type ReceivableMapping = {
  sourceId: string;
  accountId: string;
  periodId: string;
  lineMappings: {
    lineIndex: number;
    account: string;
    costCodeId: string | null;
    fund: string;
    reserveFundId: string | null;
    originalType?: string;
  }[];
};
export type PayableMapping = Omit<ReceivableMapping, "accountId">;
export type Payment = {
  sourceId: string;
  bankTransactionId: string | null;
  date: string;
  amount: string;
  applications: { invoiceSourceId: string; amount: string; effectiveDate: string | null }[];
};
export type PaymentMapping = {
  sourceId: string;
  bankTransactionId: string;
  accountId?: string;
  periodId?: string;
  projectionPolicy?: "Original application dates" | "Later of cash and invoice dates";
  paymentItems?: { invoiceSourceId: string; paymentItemId: string }[];
  bankWitness?: {
    originalBankParentId: string;
    originalStatementLineId: string;
    statementDate: string;
    wholeBankAmount: string;
    checksum: string;
  };
};
export type StructureMapping = {
  snapshotAsOf: "2026-10-28";
  existingScheduleId?: string;
  sourceChecksums: string[];
  periods: { startsOn: string; endsOn: string; label: string }[];
  units: { premisesId: string; sourceIds: string[]; reference?: string; basis: string }[];
  otherAccounts: { originalParty: string; sourceIds: string[]; reference?: string; basis: string }[];
  reserveFunds?: { code: string; name: string; sourceAccounts: string[]; basis: string }[];
};
export type StructureResult = {
  scopeId: string;
  scheduleId: string;
  accounts: { accountId: string; premisesId: string | null; reference: string; sourceIds: string[]; kind: string }[];
  periods: { id: string; startsOn: string; endsOn: string; status: string }[];
  funds?: { id: string; code: string; name: string; sourceAccounts: string[]; status: "Draft" }[];
};
export type SourceEvidence = {
  sourceId: string;
  sources: { file: string; checksum: string }[];
  selectedChecksum?: string;
};
export type OriginalEventMapping = {
  sourceId: string;
  sourceChecksum: string;
  lineMappings: (ReceivableMapping["lineMappings"][number] & {
    bankAccountId?: string | null;
    originalBankAccountId?: string | null;
  })[];
  sourceOnlyBanks?: {
    sourceAccount: string;
    originalBankAccountId: string;
    historyChecksum: string;
    basis: "Retained archived Xero bank accounting; bank statements unavailable";
  }[];
  bankLinks: {
    lineIndex: number;
    bankTransactionId: string;
    witness: {
      originalBankParentId: string;
      originalStatementLineId: string;
      statementDate: string;
      wholeBankAmount: string;
      checksum: string;
    };
  }[];
};
export type CashGroupMapping = {
  reference: string;
  bankAccountId: string;
  payments: { sourceId: string; sourceChecksum: string }[];
  fees: { sourceId: string; sourceChecksum: string; costCodeId: string }[];
  nativeTransactionIds: string[];
  bankWitnesses: {
    originalBankParentId: string;
    originalStatementLineId: string;
    statementDate: string;
    wholeBankAmount: string;
    checksum: string;
  }[];
};
export type SupplierCashMapping = {
  sourceId: string;
  sourceChecksum: string;
  bankTransactionId: string;
  creditSourceId?: string;
  bankWitness: {
    originalBankParentId: string;
    originalStatementLineId: string;
    statementDate: string;
    wholeBankAmount: string;
    checksum: string;
  };
};
export type PayableNettingMapping = {
  reference: string;
  bankTransactionId: string;
  bankWitness: SupplierCashMapping["bankWitness"];
  payments: { sourceId: string; sourceChecksum: string }[];
  adjustment: { sourceId: string; sourceChecksum: string; costCodeId: string };
};
export type SupplierSourceCashMapping = {
  reference: string;
  bankTransactionId: string;
  bankWitness: SupplierCashMapping["bankWitness"];
  projectionPolicy: "Later of original cash and invoice dates";
  payments: { sourceId: string; sourceChecksum: string }[];
};
export type CustomerCashMapping = {
  sourceId: string;
  premisesId: string;
  originalDate: string;
  amount: string;
  sourceChecksum: string;
  bankTransactionId: string;
  accountId?: string | undefined;
  periodId?: string | undefined;
  sourceCreditId?: string;
  bankWitness: {
    originalBankParentId: string;
    originalStatementLineId: string;
    statementDate: string;
    wholeBankAmount: string;
    checksum: string;
  };
  originalApplications: {
    sourceId: string;
    invoiceSourceId: string;
    date: string;
    amount: string;
    sourceChecksum: string;
  }[];
};
export type Packet = {
  history: {
    buildingId: string;
    organisation: string;
    selector: string;
    accountMappings?: unknown[];
    invoices: Invoice[];
    events: { sourceId: string; lines: { sourceType: string; accountName: string }[] }[];
    payments?: Payment[];
    allocations?: CreditApplication[];
  };
  invoiceEvidence: SourceEvidence[];
  paymentEvidence?: SourceEvidence[];
  receivableMappings?: ReceivableMapping[];
  payableMappings?: PayableMapping[];
  paymentMappings?: PaymentMapping[];
  customerCashMappings?: CustomerCashMapping[];
  originalEventMappings?: OriginalEventMapping[];
  cashGroupMappings?: CashGroupMapping[];
  supplierCashMappings?: SupplierCashMapping[];
  payableNettingMappings?: PayableNettingMapping[];
  supplierSourceCashMappings?: SupplierSourceCashMapping[];
  structureMapping?: StructureMapping;
};
export type Evidence = { documentId: string; versionId: string; checksum: string; title: string };
export type Result = { canonicalId?: string; error?: unknown; review?: string };
